import {
  ProductUsageBuildingItemOUT,
  ProductUsageComponentItemOUT,
  ProductUsageSummaryOUT,
  buildingProductEditorApi,
} from "api-client";
import { makeAutoObservable } from "mobx";

export type ProductUsageView = "components" | "buildings";

interface UsageList<T> {
  count: number;
  items: T[];
  loading: boolean;
}

interface UsageModal {
  open: boolean;
  view: ProductUsageView;
  /** When true the drill-down acts as a save-confirmation dialog. */
  footer: boolean;
  /** Invoked by the save-confirmation footer (product editor only). */
  onSave?: () => void;
}

const emptyList = <T>(): UsageList<T> => ({
  count: 0,
  items: [],
  loading: false,
});

/**
 * Shared state for the Product Usage warning, used by both the building product
 * editor and the component editor. Products and components resolve through the
 * same `product_usage` endpoints (a component id is accepted as a product id),
 * so a single store backs the one pluggable `<ProductUsageWarning />`.
 */
class ProductUsageStore {
  /** The product or component id the current summary/lists belong to. */
  id = "";
  summary: ProductUsageSummaryOUT | null = null;
  buildings: UsageList<ProductUsageBuildingItemOUT> = emptyList();
  components: UsageList<ProductUsageComponentItemOUT> = emptyList();
  modal: UsageModal = { open: false, view: "buildings", footer: false };

  constructor() {
    makeAutoObservable(this);
  }

  /** True when this product/component is used in at least one building. */
  get isUsedInBuildings() {
    return (this.summary?.building_count ?? 0) > 0;
  }

  /** True when this product/component is used in at least one component. */
  get isUsedInComponents() {
    return (this.summary?.component_count ?? 0) > 0;
  }

  /** True when used somewhere — otherwise no warning shows. */
  get isUsedAnywhere() {
    return this.isUsedInBuildings || this.isUsedInComponents;
  }

  setSummary(summary: ProductUsageSummaryOUT | null) {
    this.summary = summary;
  }

  setBuildings(list: UsageList<ProductUsageBuildingItemOUT>) {
    this.buildings = list;
  }

  setComponents(list: UsageList<ProductUsageComponentItemOUT>) {
    this.components = list;
  }

  openList(view: ProductUsageView) {
    this.modal = { open: true, view, footer: false };
  }

  openSaveConfirmation(view: ProductUsageView, onSave: () => void) {
    this.modal = { open: true, view, footer: true, onSave };
  }

  closeModal() {
    this.modal = {
      ...this.modal,
      open: false,
      footer: false,
      onSave: undefined,
    };
  }

  reset() {
    this.id = "";
    this.summary = null;
    this.buildings = emptyList();
    this.components = emptyList();
    this.modal = { open: false, view: "buildings", footer: false };
  }

  async loadSummary(id: string) {
    this.id = id;
    return await buildingProductEditorApi()
      .buildingProductsApiV1RoutersProductEditorGetProductUsageSummary(id)
      .then((response) => {
        // Discard if the id changed (faster-resolving newer request) while
        // this one was in flight, so stale data can't overwrite the active one.
        if (this.id !== id) return;
        this.setSummary(response.data);
      })
      .catch((error) => console.error("loadProductUsageSummary", error));
  }

  async fetchBuildings(id: string, pageNumber: number, pageSize: number) {
    return await buildingProductEditorApi()
      .buildingProductsApiV1RoutersProductEditorGetProductUsageBuildings(
        id,
        pageNumber,
        pageSize
      )
      .then((response) => response.data)
      .catch((error) => console.error("fetchProductUsageBuildings", error));
  }

  async fetchComponents(id: string, pageNumber: number, pageSize: number) {
    return await buildingProductEditorApi()
      .buildingProductsApiV1RoutersProductEditorGetProductUsageComponents(
        id,
        pageNumber,
        pageSize
      )
      .then((response) => response.data)
      .catch((error) => console.error("fetchProductUsageComponents", error));
  }

  static instance: ProductUsageStore;
  static getInstance(): ProductUsageStore {
    if (!ProductUsageStore.instance) {
      ProductUsageStore.instance = new ProductUsageStore();
    }
    return ProductUsageStore.instance;
  }
}

export const productUsageStore = ProductUsageStore.getInstance();
